import type {
  DocusignSignatureResponse,
  Proposal,
  ProposalSignatureMethod,
} from '@/services/proposals/types';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import {
  isDigitalEnvelopeBlockingPhysical,
  isPhysicalSignatureMethodLocked,
  resolveCapitalGlobalLockedSignatureMethod,
} from './capital-global-docusign-signature.util';

type SignatureMethodState = {
  method: ProposalSignatureMethod | null;
  locked: boolean;
} | null;

export function isCapitalGlobalProposalEditable(input: {
  proposal: Pick<Proposal, 'statusPortal'>;
  signature?: DocusignSignatureResponse | null;
  signatureMethod?: SignatureMethodState;
}): boolean {
  const statusPortal = Number(input.proposal.statusPortal);
  if (statusPortal !== PortalStatus.ProposalInProgress) {
    return false;
  }

  const lockedMethod = resolveCapitalGlobalLockedSignatureMethod({
    signatureMethod: input.signatureMethod,
    signature: input.signature ?? null,
  });

  if (lockedMethod) {
    return false;
  }

  if (
    isDigitalEnvelopeBlockingPhysical(input.signature ?? null, lockedMethod)
  ) {
    return false;
  }

  return true;
}

export function shouldOpenCapitalGlobalSignatureStep(input: {
  proposal: Pick<Proposal, 'statusPortal'>;
  signature?: DocusignSignatureResponse | null;
  signatureMethod?: SignatureMethodState;
}): boolean {
  const statusPortal = Number(input.proposal.statusPortal);
  if (statusPortal === PortalStatus.ProposalInSignature) {
    return true;
  }

  return !isCapitalGlobalProposalEditable(input);
}

export function isCapitalGlobalPhysicalSignatureUploadPending(input: {
  signature?: DocusignSignatureResponse | null;
  signatureMethod?: SignatureMethodState;
  hasSignedProposal: boolean;
}): boolean {
  const lockedMethod = resolveCapitalGlobalLockedSignatureMethod({
    signatureMethod: input.signatureMethod,
    signature: input.signature ?? null,
  });

  if (!isPhysicalSignatureMethodLocked(lockedMethod)) {
    return false;
  }

  if (isDigitalEnvelopeBlockingPhysical(input.signature ?? null, lockedMethod)) {
    return false;
  }

  return !input.hasSignedProposal;
}
